import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { LoadingButton } from '@mui/lab';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	LinearProgress,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	Switch,
	Tooltip,
	Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { alpha } from '@mui/material/styles';
import { getLanguageLabel, JOB_STAGES } from './constants';
import {
	buildMetadataState,
	getJobDisplayStage,
	isCancellingStage,
	formatDateTime,
	getModalBorder,
	getProgressValue,
	getResumeStageOptions,
	getStageLabel,
	getTranscodeUrl,
	canRetryJob,
	isCompletedStage,
	isFailedStage,
	isCancelledStage,
	isProcessingStage,
	isTerminalStage,
	renderValue
} from './helpers';
import UrlRow from './UrlRow';
import useStyles from './jobDetailsDialogStyles';

const formatStageLabel = (value) => {
	if (!value) {
		return '-';
	}

	return String(value)
		.split('_')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

const getHeaderClassName = (stage, classes) => {
	if (isCompletedStage(stage)) return `${classes.header} ${classes.headerCompleted}`;
	if (isFailedStage(stage)) return `${classes.header} ${classes.headerFailed}`;
	if (isCancellingStage(stage)) return `${classes.header} ${classes.headerCancelling}`;
	if (isCancelledStage(stage)) return `${classes.header} ${classes.headerCancelled}`;
	return `${classes.header} ${classes.headerDefault}`;
};

const getStageChipClassName = (stage, classes) => {
	if (isCompletedStage(stage)) return `${classes.stageChip} ${classes.stageChipCompleted}`;
	if (isFailedStage(stage)) return `${classes.stageChip} ${classes.stageChipFailed}`;
	if (isCancellingStage(stage)) return `${classes.stageChip} ${classes.stageChipCancelling}`;
	if (isCancelledStage(stage)) return `${classes.stageChip} ${classes.stageChipCancelled}`;
	if (isProcessingStage(stage)) return `${classes.stageChip} ${classes.stageChipProcessing}`;
	return classes.stageChip;
};

const getProgressBarClassName = (stage, classes) => {
	if (isCompletedStage(stage)) return `${classes.progressBar} ${classes.progressBarCompleted}`;
	if (isCancellingStage(stage)) return `${classes.progressBar} ${classes.progressBarCancelling}`;
	if (isCancelledStage(stage)) return `${classes.progressBar} ${classes.progressBarCancelled}`;
	if (isFailedStage(stage)) return `${classes.progressBar} ${classes.progressBarFailed}`;
	return `${classes.progressBar} ${classes.progressBarDefault}`;
};

const getIndeterminateProgressClassName = (stage, classes) => {
	if (isCompletedStage(stage)) {
		return `${classes.progressBarIndeterminate} ${classes.progressBarIndeterminateCompleted}`;
	}
	if (isFailedStage(stage)) {
		return `${classes.progressBarIndeterminate} ${classes.progressBarIndeterminateFailed}`;
	}
	if (isCancellingStage(stage)) {
		return `${classes.progressBarIndeterminate} ${classes.progressBarIndeterminateCancelling}`;
	}
	if (isCancelledStage(stage)) {
		return `${classes.progressBarIndeterminate} ${classes.progressBarIndeterminateCancelled}`;
	}
	if (isProcessingStage(stage)) {
		return `${classes.progressBarIndeterminate} ${classes.progressBarIndeterminateProcessing}`;
	}
	return `${classes.progressBarIndeterminate} ${classes.progressBarIndeterminateDefault}`;
};

const JobMetricCard = ({ label, value, mono = false }) => (
	<Box
		sx={{
			px: 1.5,
			py: 1.1,
			borderRadius: 1.5,
			border: '1px solid',
			borderColor: (theme) => getModalBorder(theme),
			background: (theme) =>
				theme.palette.mode === 'dark'
					? alpha(theme.palette.common.white, 0.025)
					: alpha(theme.palette.common.black, 0.018),
			minHeight: 48,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			transition: 'border-color 0.15s ease',
			'&:hover': {
				borderColor: (theme) =>
					theme.palette.mode === 'dark'
						? 'rgba(255, 255, 255, 0.2)'
						: 'rgba(15, 23, 42, 0.18)'
			}
		}}
	>
		<Typography
			variant="caption"
			sx={{
				color: 'text.secondary',
				fontWeight: 600,
				letterSpacing: '0.06em',
				textTransform: 'uppercase',
				fontSize: '1.3rem'
			}}
		>
			{label}
		</Typography>
		<Typography
			variant="body2"
			fontWeight={700}
			sx={{
				mt: 0.35,
				wordBreak: 'break-word',
				fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
				fontSize: '1.5rem',
				lineHeight: 1.3
			}}
		>
			{renderValue(value)}
		</Typography>
	</Box>
);

const JobDetailsDialog = ({
	open,
	onClose,
	job,
	jobDetails,
	loading,
	error,
	onCopyUrl,
	onSaveMetadata,
	onHardReset,
	onRetry,
	metadataSaving,
	hardResetting,
	retrying,
	isExperimentalUser,
	isFeatureEnabled
}) => {
	const classes = useStyles();
	const detailRecord = useMemo(() => jobDetails || job || {}, [jobDetails, job]);
	const displayStage = getJobDisplayStage(detailRecord);
	const progressValue = getProgressValue(detailRecord.progress);
	const hasDetailRecord = Object.keys(detailRecord).length > 0;
	const isInFlight = Boolean(detailRecord.stage) && !isTerminalStage(detailRecord.stage);
	const transcodeUrl = getTranscodeUrl(detailRecord);
	const isRetryEditable = detailRecord.stage === JOB_STAGES.FAILED || detailRecord.stage === JOB_STAGES.CANCELLED;
	const [metadataForm, setMetadataForm] = useState(buildMetadataState(detailRecord));

	const initialMetadataForm = useMemo(() => buildMetadataState(detailRecord), [detailRecord]);
	const resumeStageOptions = useMemo(
		() => getResumeStageOptions(initialMetadataForm.lastCompletedStage),
		[initialMetadataForm.lastCompletedStage]
	);
	const hasUnsavedChanges = JSON.stringify(metadataForm) !== JSON.stringify(initialMetadataForm);

	useEffect(() => {
		setMetadataForm(initialMetadataForm);
	}, [initialMetadataForm]);

	const overviewMetrics = [
		{ label: 'Site ID', value: detailRecord.siteId, mono: true },
		{ label: 'Language', value: detailRecord.language ? getLanguageLabel(detailRecord.language) : null },
		{ label: 'Video Template', value: detailRecord.compositionId },
		{ label: 'Last Completed Stage', value: formatStageLabel(detailRecord.lastCompletedStage) }
	].filter((item) => item.value !== null && typeof item.value !== 'undefined' && item.value !== '');

	const timelineItems = [
		{ label: 'Created At', value: formatDateTime(detailRecord.createdAt) },
		{ label: 'Last Executed At', value: formatDateTime(detailRecord.lastExecutedAt) },
	];

	const handleMetadataToggle = (key) => (event) => {
		setMetadataForm((prev) => ({ ...prev, [key]: event.target.checked }));
	};

	const handleResumeStageChange = (event) => {
		setMetadataForm((prev) => ({ ...prev, lastCompletedStage: event.target.value }));
	};

	const handleSaveMetadata = async () => {
		if (!detailRecord.id || !hasUnsavedChanges) {
			return;
		}

		await onSaveMetadata(detailRecord.id, {
			debug: metadataForm.debug,
			skipDelivery: metadataForm.skipDelivery,
			muted: metadataForm.muted,
			lastCompletedStage: metadataForm.lastCompletedStage || null
		});
	};

	const handleHardResetClick = async () => {
		if (!detailRecord.id) {
			return;
		}

		const shouldContinue = window.confirm(
			'Hard reset will clear existing job artifacts and restart the job from scratch. Do you want to continue?'
		);

		if (!shouldContinue) {
			return;
		}

		await onHardReset(detailRecord.id);
	};

	const handleRetryClick = async () => {
		if (!detailRecord.id || !onRetry) {
			toast.error('Unable to retry this job', { position: toast.POSITION.BOTTOM_RIGHT });
			return;
		}

		await onRetry(detailRecord.id);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				className: classes.dialogPaper
			}}
		>
			<Box className={getHeaderClassName(displayStage, classes)}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
					spacing={1.5}
					sx={{ position: 'relative', zIndex: 1 }}
				>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Stack direction="row" alignItems="center" spacing={0.75}>
							<Typography
								variant="overline"
								sx={{
									opacity: 0.6,
									letterSpacing: '0.18em',
									fontWeight: 700,
									fontSize: '1.2rem'
								}}
							>
								RSS Video Job
							</Typography>
						</Stack>
						<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5, minWidth: 0 }}>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{
									lineHeight: 1.25,
									fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
									fontSize: { xs: '1.6rem', sm: '1.75rem' },
									letterSpacing: '-0.02em',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap'
								}}
							>
								{detailRecord.id || (loading ? 'Loading...' : 'Unknown Job')}
							</Typography>
							{detailRecord.id && (
								<Tooltip title="Copy job ID">
									<IconButton
										size="small"
										onClick={() => onCopyUrl(detailRecord.id)}
										sx={{
											color: 'inherit',
											opacity: 0.65,
											p: 0.4,
											'&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.08)' }
										}}
									>
										<ContentCopyIcon sx={{ fontSize: 14 }} />
									</IconButton>
								</Tooltip>
							)}
						</Stack>
						<Typography variant="body2" sx={{ mt: 0.5, opacity: 0.55, fontSize: '1.3rem', fontWeight: 500 }}>
							{[
								detailRecord.siteId ? `Site ${detailRecord.siteId}` : null,
								detailRecord.language ? getLanguageLabel(detailRecord.language) : null,
								detailRecord.compositionId || null
							]
								.filter(Boolean)
								.join('  \u2022  ') || 'Job metadata'}
						</Typography>
					</Box>

					<Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: 0.5 }}>
						{detailRecord.stage && (
							<Chip
								label={getStageLabel(displayStage)}
								size="small"
								className={getStageChipClassName(displayStage, classes)}
							/>
						)}
					</Stack>
				</Stack>

				<Box sx={{ mt: 2, position: 'relative', zIndex: 1 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
						<Typography variant="body2" sx={{ opacity: 0.7, fontWeight: 600, fontSize: '1.3rem' }}>
							Progress
						</Typography>
						<Typography
							variant="body2"
							fontWeight={700}
							sx={{
								fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
								fontSize: '1.3rem',
								opacity: 0.9
							}}
						>
							{progressValue !== null ? `${progressValue}%` : renderValue(detailRecord.progress)}
						</Typography>
					</Stack>
					{progressValue !== null ? (
						<LinearProgress
							variant="determinate"
							value={progressValue}
							className={getProgressBarClassName(displayStage, classes)}
						/>
					) : isTerminalStage(detailRecord.stage) ? (
						<LinearProgress
							variant="determinate"
							value={isCompletedStage(detailRecord.stage) ? 100 : 0}
							className={getProgressBarClassName(displayStage, classes)}
						/>
					) : (
						isInFlight && (
							<LinearProgress className={getIndeterminateProgressClassName(displayStage, classes)} />
						)
					)}
				</Box>
			</Box>

			<DialogContent
				sx={{
					p: 0,
					flex: '1 1 auto',
					minHeight: 0,
					overflowY: 'auto',
					position: 'relative',
					zIndex: 1,
					bgcolor: 'background.paper'
				}}
			>
				{loading && (
					<Box sx={{ px: 2.5, py: 2 }}>
						<Stack spacing={1.5}>
							<Stack direction="row" spacing={1.25} alignItems="center">
								<CircularProgress size={16} thickness={5} />
								<Typography color="text.secondary" fontWeight={600} variant="body2" sx={{ fontSize: '1.3rem' }}>
									Fetching latest job details...
								</Typography>
							</Stack>
							<Grid container spacing={1}>
								{[0, 1, 2, 3].map((item) => (
									<Grid item xs={12} sm={6} key={item}>
										<Skeleton variant="rounded" height={64} sx={{ borderRadius: 1.5 }} />
									</Grid>
								))}
							</Grid>
						</Stack>
					</Box>
				)}

				{!loading && error && (
					<Alert severity="warning" sx={{ mx: 2.5, mt: 2, borderRadius: 1.5 }}>
						Failed to load full job details. Showing available row data only.
						{error ? ` ${error}` : ''}
					</Alert>
				)}

				{!loading && !hasDetailRecord && !error && (
					<Alert severity="info" sx={{ mx: 2.5, my: 2, borderRadius: 1.5 }}>
						No job data available.
					</Alert>
				)}

				{!loading && hasDetailRecord && (
					<Box sx={{ px: 2.5, py: 2.25 }}>
						{overviewMetrics.length > 0 && (
							<Box sx={{ mb: 2.25 }}>
								<Typography
									variant="overline"
									sx={{
										letterSpacing: '0.12em',
										color: 'text.secondary',
										fontWeight: 700,
										display: 'block',
										mb: 1,
										fontSize: '1.35rem'
									}}
								>
									Overview
								</Typography>
								<Grid container spacing={1}>
									{overviewMetrics.map((metric) => (
										<Grid item xs={12} sm={6} key={metric.label}>
											<JobMetricCard label={metric.label} value={metric.value} mono={metric.mono} />
										</Grid>
									))}
								</Grid>
							</Box>
						)}

						<Box sx={{ mb: 2.25 }}>
							<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
								<ScheduleOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', opacity: 0.7 }} />
								<Typography
									variant="overline"
									sx={{
										letterSpacing: '0.12em',
										color: 'text.secondary',
										fontWeight: 700,
										fontSize: '1.35rem'
									}}
								>
									Timeline
								</Typography>
							</Stack>
							<Box
								sx={{
									border: '1px solid',
									borderColor: (theme) => getModalBorder(theme),
									borderRadius: 1.5,
									overflow: 'hidden'
								}}
							>
								{timelineItems.map((item, index) => (
									<Stack
										key={item.label}
										direction="row"
										justifyContent="space-between"
										alignItems="center"
										sx={{
											px: 1.5,
											py: 1,
											gap: 1,
											borderBottom: index < timelineItems.length - 1 ? '1px solid' : 'none',
											borderColor: (theme) => getModalBorder(theme),
											bgcolor: (theme) =>
												index % 2 === 0
													? 'transparent'
													: theme.palette.mode === 'dark'
														? alpha(theme.palette.common.white, 0.02)
														: alpha(theme.palette.common.black, 0.015)
										}}
									>
										<Typography
											variant="caption"
											sx={{
												color: 'text.secondary',
												fontWeight: 600,
												letterSpacing: '0.05em',
												textTransform: 'uppercase',
												fontSize: '1.2rem'
											}}
										>
											{item.label}
										</Typography>
										<Typography
											variant="body2"
											fontWeight={600}
											sx={{
												fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
												fontSize: '1.35rem'
											}}
										>
											{item.value}
										</Typography>
									</Stack>
								))}
							</Box>
						</Box>

						{(detailRecord.pageURL || transcodeUrl) && (
							<Box sx={{ mb: 2.25 }}>
								<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
									<LinkOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', opacity: 0.7 }} />
									<Typography
										variant="overline"
										sx={{
											letterSpacing: '0.12em',
											color: 'text.secondary',
											fontWeight: 700,
											fontSize: '1.35rem'
										}}
									>
										Links
									</Typography>
								</Stack>
								<Stack spacing={1}>
									<UrlRow label="Page URL" url={detailRecord.pageURL} onCopyUrl={onCopyUrl} />
									<UrlRow label="Transcode URL" url={transcodeUrl} onCopyUrl={onCopyUrl} />
								</Stack>
							</Box>
						)}

						{isExperimentalUser && (
							<Accordion
							disableGutters
							elevation={0}
							sx={{
								mb: 2,
								border: '1px solid',
								borderColor: (theme) => getModalBorder(theme),
								borderRadius: '10px !important',
								overflow: 'hidden',
								'&:before': { display: 'none' },
								bgcolor: 'transparent'
							}}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								sx={{
									px: 1.5,
									minHeight: 48,
									'& .MuiAccordionSummary-content': {
										my: 0.85,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: 1,
										flexWrap: 'wrap'
									}
								}}
							>
								<Box>
									<Typography fontWeight={700} variant="body2" sx={{ fontSize: '1.4rem' }}>
										Edit
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, fontSize: '1.25rem' }}>
										Edit retry metadata for failed or cancelled jobs.
									</Typography>
								</Box>
								{hasUnsavedChanges && <Chip label="Unsaved changes" color="warning" size="small" />}
							</AccordionSummary>
							<AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
								<Box sx={{ pt: 0.5 }}>
									<Stack spacing={1.5}>
										<Box>
											<Typography variant="body2" fontWeight={700} sx={{ fontSize: '1.4rem' }}>
												Edit Before Retry
											</Typography>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, fontSize: '1.3rem' }}>
												Update flags on failed/cancelled jobs. Changes apply on the next retry.
											</Typography>
										</Box>

									<Grid container spacing={1}>
										{[
											{ key: 'debug', label: 'Debug' },
											{ key: 'skipDelivery', label: 'Skip Delivery' },
											{ key: 'muted', label: 'Muted' }
										].map((field) => (
											<Grid item xs={12} sm={4} key={field.key}>
												<Box
													sx={{
														border: '1px solid',
														borderColor: (theme) => getModalBorder(theme),
														borderRadius: 1.25,
														px: 1,
														py: 0.35
													}}
												>
													<FormControlLabel
														control={
															<Switch
																checked={metadataForm[field.key]}
																onChange={handleMetadataToggle(field.key)}
																disabled={!isRetryEditable || metadataSaving}
															/>
														}
														label={field.label}
														sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '1.35rem', fontWeight: 600 } }}
													/>
												</Box>
											</Grid>
										))}
									</Grid>

									<Box>
										<Typography variant="body2" fontWeight={700} sx={{ mb: 0.75, fontSize: '1.4rem' }}>
											Resume from ({renderValue(formatStageLabel(initialMetadataForm.lastCompletedStage))})
										</Typography>
										<FormControl fullWidth size="small">
											<Select
												value={metadataForm.lastCompletedStage}
												onChange={handleResumeStageChange}
												disabled={!isRetryEditable || metadataSaving || resumeStageOptions.length === 0}
												displayEmpty
											>
												{resumeStageOptions.length === 0 ? (
													<MenuItem value="">No stage available</MenuItem>
												) : (
													resumeStageOptions.map((stage) => (
														<MenuItem key={stage} value={stage}>
															{formatStageLabel(stage)}
														</MenuItem>
													))
												)}
											</Select>
										</FormControl>
									</Box>

									{!isRetryEditable && (
										<Alert severity="info" sx={{ borderRadius: 1.25 }}>
											Metadata edits are only available for failed or cancelled jobs.
										</Alert>
									)}

									{hasUnsavedChanges && (
										<Alert severity="warning" sx={{ borderRadius: 1.25 }}>
											Unsaved changes. Save metadata before retry to use the updated values.
										</Alert>
									)}

									<LoadingButton
										variant="contained"
										onClick={handleSaveMetadata}
										loading={metadataSaving}
										disabled={!isRetryEditable || !hasUnsavedChanges}
										sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
									>
										Save MetaData
									</LoadingButton>

									<Box
										sx={{
											borderTop: '1px solid',
											borderColor: (theme) => getModalBorder(theme),
											pt: 1.5
										}}
									>
										<Stack spacing={1}>
											<Stack direction="row" spacing={0.75} alignItems="center">
												<WarningAmberRoundedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
												<Typography variant="body2" fontWeight={700} sx={{ fontSize: '1.4rem' }}>
													Hard Reset
												</Typography>
											</Stack>
											<Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3rem' }}>
												Hard reset clears existing artifacts and restarts the job from scratch. Use only when you do not want to resume from a partial stage.
											</Typography>
											<LoadingButton
												variant="outlined"
												color="error"
												onClick={handleHardResetClick}
												loading={hardResetting}
												disabled={!isFeatureEnabled || !isRetryEditable}
												sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
											>
												Hard Reset Job
											</LoadingButton>
										</Stack>
									</Box>
									</Stack>
								</Box>
							</AccordionDetails>
							</Accordion>
						)}

						<Accordion
							disableGutters
							elevation={0}
							sx={{
								border: '1px solid',
								borderColor: (theme) => getModalBorder(theme),
								borderRadius: '10px !important',
								overflow: 'hidden',
								'&:before': { display: 'none' },
								bgcolor: 'transparent'
							}}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								sx={{
									px: 1.5,
									minHeight: 40,
									'& .MuiAccordionSummary-content': { my: 0.75 }
								}}
							>
								<Typography fontWeight={700} variant="body2" sx={{ fontSize: '1.4rem' }}>
									Raw payload
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
								<Box
									component="pre"
									sx={{
										m: 0,
										p: 1.5,
										borderRadius: 1.25,
										backgroundColor: (theme) =>
											theme.palette.mode === 'dark' ? alpha('#000', 0.35) : '#f4f5f7',
										color: (theme) =>
											theme.palette.mode === 'dark' ? 'grey.300' : 'grey.800',
										overflowX: 'auto',
										fontSize: 12.5,
										lineHeight: 1.5,
										fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
										border: '1px solid',
										borderColor: (theme) => getModalBorder(theme)
									}}
								>
									{JSON.stringify(detailRecord, null, 2)}
								</Box>
							</AccordionDetails>
						</Accordion>
					</Box>
				)}
			</DialogContent>

			<Divider sx={{ borderColor: (theme) => getModalBorder(theme), flexShrink: 0 }} />
			<DialogActions
				sx={{
					px: 2.5,
					py: 1.5,
					justifyContent: 'flex-end',
					alignItems: 'center',
					flexShrink: 0,
					gap: 1
				}}
			>
				{canRetryJob(detailRecord) && (
					<LoadingButton
						onClick={handleRetryClick}
						variant="contained"
						loading={retrying}
						disabled={!isFeatureEnabled || hasUnsavedChanges || hardResetting}
						startIcon={<ReplayOutlinedIcon sx={{ fontSize: '18px !important' }} />}
						sx={{
							minWidth: 96,
							fontWeight: 700,
							borderRadius: 1.5,
							textTransform: 'none',
							fontSize: '1.4rem'
						}}
					>
						Retry
					</LoadingButton>
				)}
				<LoadingButton
					onClick={onClose}
					variant="contained"
					startIcon={<CloseRoundedIcon sx={{ fontSize: '18px !important' }} />}
					sx={{
						minWidth: 96,
						fontWeight: 700,
						borderRadius: 1.5,
						textTransform: 'none',
						fontSize: '1.4rem',
						bgcolor: (theme) =>
							theme.palette.mode === 'dark'
								? 'rgba(255,255,255,0.08)'
								: 'rgba(15, 23, 42, 0.06)',
						color: 'text.primary',
						border: '1px solid',
						borderColor: (theme) => getModalBorder(theme),
						boxShadow: 'none',
						'&:hover': {
							bgcolor: (theme) =>
								theme.palette.mode === 'dark'
									? 'rgba(255,255,255,0.12)'
									: 'rgba(15, 23, 42, 0.1)',
							boxShadow: 'none'
						}
					}}
				>
					Close
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default JobDetailsDialog;
